/**
 * Students API Service
 * Manages student data and progress
 */

import { sessionManagerApi, labManagerApi, environmentOrchestratorApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import type { Student, StudentDetail, StudentProgress, PaginatedResponse } from '../types';

interface StudentListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'idle' | 'offline';
  sortBy?: 'name' | 'lastActive' | 'progress' | 'score';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Get paginated list of students
 */
export async function getStudents(params: StudentListParams = {}): Promise<PaginatedResponse<Student>> {
  try {
    const response = await sessionManagerApi.get('/instructor/students', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch students:', error);
    // Return mock data for development
    const mockStudents: Student[] = [
      {
        id: '1',
        email: 'john.doe@example.com',
        firstName: 'John',
        lastName: 'Doe',
        status: 'active',
        enrolledCourses: 3,
        labsCompleted: 12,
        labsInProgress: 2,
        averageScore: 87,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
        createdAt: '2024-01-15T10:00:00Z',
      },
      {
        id: '2',
        email: 'jane.smith@example.com',
        firstName: 'Jane',
        lastName: 'Smith',
        status: 'active',
        enrolledCourses: 2,
        labsCompleted: 8,
        labsInProgress: 1,
        averageScore: 92,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        createdAt: '2024-02-01T14:00:00Z',
      },
      {
        id: '3',
        email: 'bob.johnson@example.com',
        firstName: 'Bob',
        lastName: 'Johnson',
        status: 'idle',
        enrolledCourses: 4,
        labsCompleted: 18,
        labsInProgress: 0,
        averageScore: 78,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
        createdAt: '2023-11-20T09:00:00Z',
      },
      {
        id: '4',
        email: 'alice.brown@example.com',
        firstName: 'Alice',
        lastName: 'Brown',
        status: 'active',
        enrolledCourses: 2,
        labsCompleted: 5,
        labsInProgress: 3,
        averageScore: 95,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
        createdAt: '2024-03-01T11:00:00Z',
      },
      {
        id: '5',
        email: 'charlie.wilson@example.com',
        firstName: 'Charlie',
        lastName: 'Wilson',
        status: 'offline',
        enrolledCourses: 1,
        labsCompleted: 3,
        labsInProgress: 0,
        averageScore: 72,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
        createdAt: '2024-02-15T16:00:00Z',
      },
      {
        id: '6',
        email: 'diana.lee@example.com',
        firstName: 'Diana',
        lastName: 'Lee',
        status: 'active',
        enrolledCourses: 3,
        labsCompleted: 15,
        labsInProgress: 1,
        averageScore: 89,
        lastActiveAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
        createdAt: '2024-01-05T08:00:00Z',
      },
    ];

    // Apply search filter
    let filtered = mockStudents;
    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.firstName.toLowerCase().includes(search) ||
          s.lastName.toLowerCase().includes(search) ||
          s.email.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (params.status) {
      filtered = filtered.filter((s) => s.status === params.status);
    }

    const mock = {
      data: filtered,
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: filtered.length,
        totalPages: Math.ceil(filtered.length / (params.limit || 20)),
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get student details by ID
 */
export async function getStudentDetail(studentId: string): Promise<StudentDetail> {
  try {
    const [studentRes, progressRes, activityRes] = await Promise.all([
      sessionManagerApi.get(`/instructor/students/${studentId}`),
      labManagerApi.get(`/instructor/students/${studentId}/progress`),
      labManagerApi.get(`/instructor/students/${studentId}/activity`, { params: { limit: 10 } }),
    ]);

    // session-manager returns { student: { id, name, email, role, status,
    // lastActiveAt, createdAt } } — no first/last split, so derive it.
    const s = studentRes.data.student ?? {};
    const nameParts = String(s.name || (s.email ? s.email.split('@')[0] : '') || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');

    // lab-manager /progress returns { progress: [{ labId, status, score,
    // updatedAt }], completed, inProgress, avgScore, total } — lab-level rows,
    // not course-level. Map each into the CourseProgress shape the UI expects;
    // no lab/course title is provided here, so fall back to a short lab ref.
    const progressItems: Array<{ labId: string; status: string; score: number | null; updatedAt: string | null }> =
      progressRes.data.progress ?? [];
    const courseProgress = progressItems.map((p) => {
      const pct = p.status === 'completed' ? 100 : p.status === 'in_progress' ? 50 : 0;
      return {
        courseId: p.labId,
        courseName: `Lab ${String(p.labId).slice(0, 8)}`,
        progress: pct,
        labsCompleted: p.status === 'completed' ? 1 : 0,
        totalLabs: 1,
        averageScore: p.score ?? 0,
        startedAt: p.updatedAt ?? '',
        lastActivityAt: p.updatedAt ?? '',
      };
    });

    // lab-manager /activity returns { activity: [{ type, labId, status,
    // timestamp }] } (not `.activities`).
    const activityItems: Array<{ labId: string; status: string; timestamp: string | null }> =
      activityRes.data.activity ?? [];
    const recentActivity = activityItems.map((a, i) => ({
      id: `act-${i}-${a.labId}`,
      type: (a.status === 'completed'
        ? 'completion'
        : a.status === 'submitted'
          ? 'submission'
          : 'start') as 'completion' | 'submission' | 'start',
      message: `${a.status === 'completed' ? 'Completed' : a.status === 'submitted' ? 'Submitted' : 'Started'} a lab`,
      labTitle: `Lab ${String(a.labId).slice(0, 8)}`,
      timestamp: a.timestamp ?? '',
    }));

    return {
      id: s.id,
      email: s.email,
      firstName,
      lastName,
      status: s.status === 'active' ? 'active' : s.status === 'idle' ? 'idle' : 'offline',
      enrolledCourses: 0,
      labsCompleted: progressRes.data.completed ?? 0,
      labsInProgress: progressRes.data.inProgress ?? 0,
      averageScore: progressRes.data.avgScore ?? 0,
      lastActiveAt: s.lastActiveAt,
      createdAt: s.createdAt,
      courseProgress,
      recentActivity,
      apiUsage: { tokensUsed: 0, tokensLimit: 0, callsToday: 0 },
    };
  } catch (error) {
    console.error('Failed to fetch student detail:', error);
    const mock: StudentDetail = {
      id: studentId,
      email: 'john.doe@example.com',
      firstName: 'John',
      lastName: 'Doe',
      status: 'active',
      enrolledCourses: 3,
      labsCompleted: 12,
      labsInProgress: 2,
      averageScore: 87,
      lastActiveAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      createdAt: '2024-01-15T10:00:00Z',
      bio: 'Software developer learning AI/ML',
      organization: 'Tech Corp',
      timezone: 'America/New_York',
      courseProgress: [
        {
          courseId: '1',
          courseName: 'LLM Fundamentals',
          progress: 75,
          labsCompleted: 6,
          totalLabs: 8,
          averageScore: 88,
          startedAt: '2024-01-20T10:00:00Z',
          lastActivityAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        },
        {
          courseId: '2',
          courseName: 'Building AI Applications',
          progress: 40,
          labsCompleted: 4,
          totalLabs: 10,
          averageScore: 85,
          startedAt: '2024-02-15T10:00:00Z',
          lastActivityAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        },
      ],
      recentActivity: [
        {
          id: '1',
          type: 'completion',
          message: 'Completed "RAG Fundamentals" with score 92%',
          labTitle: 'RAG Fundamentals',
          timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        },
        {
          id: '2',
          type: 'start',
          message: 'Started "Advanced Prompting"',
          labTitle: 'Advanced Prompting',
          timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        },
      ],
      apiUsage: {
        tokensUsed: 45000,
        tokensLimit: 100000,
        callsToday: 23,
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get student's lab progress
 */
export async function getStudentProgress(studentId: string): Promise<StudentProgress[]> {
  try {
    const response = await labManagerApi.get(`/instructor/students/${studentId}/labs`);
    // lab-manager returns { labs: [{ labId, title, status, score, updatedAt }] }.
    // Map to StudentProgress; the endpoint has no course grouping or explicit
    // submittedAt, so fall back (courseTitle → '—', submittedAt → updatedAt).
    const labs: Array<{ labId: string; title: string; status: string; score: number | null; updatedAt: string | null }> =
      response.data.labs ?? [];
    return labs.map((l) => ({
      labId: l.labId,
      labTitle: l.title,
      courseTitle: '—',
      status: l.status as StudentProgress['status'],
      progress: l.status === 'completed' ? 100 : l.status === 'in_progress' ? 50 : 0,
      score: l.score ?? undefined,
      submittedAt: l.updatedAt ?? undefined,
      timeSpent: 0,
    }));
  } catch (error) {
    console.error('Failed to fetch student progress:', error);
    const mock: StudentProgress[] = [
      {
        labId: '1',
        labTitle: 'Introduction to OpenAI API',
        courseTitle: 'LLM Fundamentals',
        status: 'completed',
        progress: 100,
        score: 92,
        feedback: 'Great work!',
        submittedAt: '2024-03-01T10:00:00Z',
        gradedAt: '2024-03-01T12:00:00Z',
        timeSpent: 45,
      },
      {
        labId: '2',
        labTitle: 'Building RAG Applications',
        courseTitle: 'Building AI Applications',
        status: 'in_progress',
        progress: 65,
        timeSpent: 30,
      },
      {
        labId: '3',
        labTitle: 'Fine-tuning Basics',
        courseTitle: 'Advanced AI',
        status: 'submitted',
        progress: 100,
        submittedAt: '2024-03-15T14:00:00Z',
        timeSpent: 90,
      },
    ];
    return mockFallback(error, mock);
  }
}

/**
 * Get student's active environments
 */
export async function getStudentEnvironments(studentId: string) {
  try {
    const response = await environmentOrchestratorApi.get(`/environments/${studentId}`);
    return response.data.environments;
  } catch (error) {
    console.error('Failed to fetch student environments:', error);
    return mockFallback(error, []);
  }
}

/**
 * Send message to student
 */
export async function sendMessageToStudent(studentId: string, message: string): Promise<void> {
  await sessionManagerApi.post(`/instructor/students/${studentId}/message`, { message });
}
